import Dexie, { Table } from 'dexie';
import { clearDroppedPhotoSession, clearSyncMeta } from './cacheStore';
import type { HazardLexicon } from './types';

/**
 * Per-user Dexie database. The cache tables are a replaceable server mirror.
 * The outbox and legacy-pending comments are local mutations and survive a
 * tenant change that wipes the cache. The hazard lexicon is tenant vocabulary:
 * a tenant change wipes it too, a cache reset keeps it.
 */

export interface CachedHazard {
  id: string;
  idKey: string;
  sortTs: number;
  inWindow?: number;
  viewedAt?: number;
  user_id?: string;
  [key: string]: unknown;
}

export interface CachedComment {
  id: string;
  incident_id: string;
  sortTs: number;
  /** 1 when the row was copied from an unsynced legacy comment. Cache wipes keep it. */
  legacyPending?: number;
  user_id?: string;
  [key: string]: unknown;
}

export interface CachedReaction {
  id: string;
  content_id: string;
  user_id?: string;
  content_type?: string;
  [key: string]: unknown;
}

export type HazardOpKind =
  | 'hazard.create'
  | 'hazard.update'
  | 'hazard.delete'
  | 'hazard.resolve';

export type OpKind =
  | HazardOpKind
  | 'comment.create'
  | 'comment.update'
  | 'comment.delete'
  | 'reaction.add'
  | 'reaction.remove';

export interface OutboxOp {
  id: string;
  kind: OpKind;
  entityId: string;
  payload: Record<string, unknown>;
  attempts: number;
  nextAttemptAt: number;
  dependsOn?: string;
  status: 'pending' | 'dead';
  createdAt: number;
}

export interface OutboxMedia {
  id: string;
  opId: string;
  filename: string;
  mimeType: string;
  blob: Blob;
}

export interface LegacySyncQueueRow {
  localId?: number;
  sourceId: number;
  reaction_id: string;
  operation: 'CREATE' | 'DELETE';
  content_type: 'card' | 'comment';
  content_id: string;
  created_at: string;
  attempts: number;
  last_attempt?: string;
  error_message?: string;
}

export interface MetaRow {
  key: string;
  value: string;
}

export class UserStore extends Dexie {
  hazards!: Table<CachedHazard, string>;
  comments!: Table<CachedComment, string>;
  reactions!: Table<CachedReaction, string>;
  outbox!: Table<OutboxOp, string>;
  outboxMedia!: Table<OutboxMedia, string>;
  legacySyncQueue!: Table<LegacySyncQueueRow, number>;
  /** Tenant vocabulary, not a server mirror: a cache reset keeps it. */
  hazardLexicon!: Table<HazardLexicon, number>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    // Version 2 adds hazardLexicon. A database already opened at version 1 gets
    // the empty table created by the upgrade; there is no data to migrate.
    this.version(2).stores({
      hazardLexicon: 'id, term, category',
    });
    this.version(1).stores({
      hazards: 'id, user_id, inWindow, viewedAt, [sortTs+idKey]',
      comments: 'id, incident_id, user_id, legacyPending',
      reactions: 'id, content_id, user_id, content_type',
      outbox: 'id, entityId, kind, status, createdAt',
      outboxMedia: 'id, opId',
      legacySyncQueue: '++localId, &sourceId',
      meta: '&key',
    });
  }

  /** Replace the whole lexicon: clear, then bulkAdd the fresh rows. */
  async replaceLexicon(rows: HazardLexicon[]): Promise<void> {
    await this.transaction('rw', this.hazardLexicon, async () => {
      await this.hazardLexicon.clear();
      if (rows.length > 0) {
        await this.hazardLexicon.bulkAdd(rows);
      }
    });
  }

  async listLexicon(): Promise<HazardLexicon[]> {
    return this.hazardLexicon.toArray();
  }
}

export interface OpenUserStoreArgs {
  host: string;
  userId: string;
  tenantId?: string | null;
}

const TENANT_META_KEY = 'tenantId';

let current: UserStore | null = null;

export function userStoreName(host: string, userId: string): string {
  return `safework-user:${encodeURIComponent(host)}:${encodeURIComponent(userId)}`;
}

export function readOfflineIdentity(): { host: string; userId: string } | null {
  if (typeof localStorage === 'undefined' || typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem('oidc_user_info');
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { userId?: unknown };
    if (typeof parsed.userId !== 'string' || parsed.userId.length === 0) {
      return null;
    }
    return { host: window.location.host, userId: parsed.userId };
  } catch {
    return null;
  }
}

/** Tenant id cached by tenantService, ignoring the freshness TTL so offline open still isolates. */
export function readCachedTenantId(userId: string): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(`tenant_info_cache_${userId}`);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { data?: { id?: unknown } };
    const id = parsed.data?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Tenant change: drop the server mirror and the tenant's lexicon, keep local mutations. */
async function wipeCacheKeepMutations(db: UserStore): Promise<void> {
  await db.transaction('rw', [db.hazards, db.comments, db.reactions, db.hazardLexicon, db.meta], async () => {
    await db.hazards.clear();
    await db.reactions.clear();
    await db.hazardLexicon.clear();
    const pending = await db.comments.filter((row) => row.legacyPending === 1).toArray();
    await db.comments.clear();
    if (pending.length > 0) {
      await db.comments.bulkPut(pending);
    }
    await clearSyncMeta(db);
  });
}

async function applyTenantGuard(db: UserStore, tenantId: string | null | undefined): Promise<void> {
  if (!tenantId) {
    return;
  }
  const stored = await db.meta.get(TENANT_META_KEY);
  if (stored && stored.value !== tenantId) {
    await wipeCacheKeepMutations(db);
  }
  if (!stored || stored.value !== tenantId) {
    await db.meta.put({ key: TENANT_META_KEY, value: tenantId });
  }
}

export async function openUserStore(args: OpenUserStoreArgs): Promise<UserStore> {
  const name = userStoreName(args.host, args.userId);
  if (current && current.name === name && current.isOpen()) {
    await applyTenantGuard(current, args.tenantId);
    return current;
  }
  if (current) {
    current.close();
    current = null;
  }
  const db = new UserStore(name);
  await db.open();
  await applyTenantGuard(db, args.tenantId);
  current = db;
  return db;
}

export async function openUserStoreFromSession(tenantId?: string | null): Promise<UserStore | null> {
  const identity = readOfflineIdentity();
  if (!identity) {
    return null;
  }
  const resolvedTenant =
    tenantId === undefined ? readCachedTenantId(identity.userId) : tenantId;
  return openUserStore({
    host: identity.host,
    userId: identity.userId,
    tenantId: resolvedTenant,
  });
}

export function getOpenUserStore(): UserStore | null {
  return current && current.isOpen() ? current : null;
}

export async function deleteUserStore(): Promise<void> {
  const identity = readOfflineIdentity();
  const name = current?.name ?? (identity ? userStoreName(identity.host, identity.userId) : null);
  if (name) {
    clearDroppedPhotoSession(name);
  }
  if (current) {
    current.close();
    current = null;
  }
  if (name) {
    await Dexie.delete(name);
  }
}
